import { NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { AuthService } from 'app/core/auth/auth.service';
import { BrandService } from 'app/core/brand/brand.service';
import { DisplayErrorComponent } from 'app/core/display-error/display-error.component';
import { DisplayErrorService } from 'app/core/display-error/display-error.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { NavigationMockApi } from 'app/mock-api/common/navigation/api';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'ekedai-layout',
    templateUrl: './ekedai.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    UserComponent,
    NgIf,
    MatIconModule,
    MatButtonModule,
    RouterOutlet,
    DisplayErrorComponent],
    styles: [`

fuse-vertical-navigation.fuse-vertical-navigation-position-left.fuse-vertical-navigation-mode-over.fuse-vertical-navigation-opened {
            transform: translate3d(0, 0, 0);
            position: absolute;
        }

        fuse-vertical-navigation.fuse-vertical-navigation-position-left.fuse-vertical-navigation-mode-over {
            left: 0;
            transform: translate3d(-100%, 0, 0);
            position: absolute;
        }

    `]
})
export class EKedaiLayoutComponent implements OnInit, OnDestroy {
    @ViewChild('scroll') scroll: ElementRef;
    _brandService = inject(BrandService);
    selectedBrand = this._brandService.currentBrand;
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    displayError: {
        type: string,
        title: string;
        message: string;
    } = null;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    /**
     * Constructor
     */
    constructor(
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _displayErrorService: DisplayErrorService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _navigationMockApi: NavigationMockApi,
        private _authService: AuthService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this._navigationMockApi.resetNavigation();

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: User) => {
                this.user = user;
                if (user && (user.role !== 'ADMIN')) {

                    this._authService.signOut();
                    // Redirect the user to the login page
                    this._router.navigate(['/sign-in']);
                }
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Subscribe to show error
        this._displayErrorService.errorMessage$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {

                if (response) {
                    this.displayError = response;
                }
                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        this._router.events
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this._navigationMockApi.resetNavigation();

                    // Call the scrollToTopNoAnimation() function
                    this.scrollToTopNoAnimation();
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    scrollToTopNoAnimation() {
        if (this.scroll) {
            const element = this.scroll.nativeElement;
            element.scrollTo({
                top: 0,
            });
        }
    }
}
